"use client";
import { useCallback, useContext, useMemo } from "react";
import classnames from "classnames";

import { useJuneSoEventTracker } from "@stadium/services/june-analytics";
import { useFlags, FeatureFlagWrapper } from "@stadium/services/launch-darkly";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import {
  ANALYTICS_EVENT_ACTION_OPENED,
  DEFAULT_EVENT_LOCATION_ANALYTICS,
} from "@stadium/services/analytics/constants/default";

import useStadiumSharedGlobalConfigContext from "@stadium/shared/src/hooks/use-global-context";

import ProfileDdBody from "@stadium/ui/src/components/ProfileDropdown/body";
import ProfileList from "@stadium/ui/src/components/ProfileDropdown/list";
import SliderDropdown from "@stadium/ui/src/components/SliderDropdown";
import Button, { LinkButton } from "@stadium/shared/src/components/shared/button";
import HeaderLink from "@stadium/shared/src/components/shared/link-tag";
import Avatar from "@stadium/shared/src/components/header/avatar";
import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";
import CreateWorkspaceModal from "@stadium/shared/src/components/header/profile-dropdown/create-workspace-modal";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import HelpCenterLink from "@stadium/shared/src/components/header/shared/help-center-link";

import {
  getMiniAccountDropdownData,
  getNavAccountDropdownData,
  getProfileData,
} from "@stadium/shared/src/helpers/accounts";
import { isStadiumSite } from "@stadium/shared/src/helpers/brands";

import {
  ACCOUNT_DROPDOWN,
  ACCOUNT_DROPDOWN_CTA_SERVICES,
} from "@stadium/shared/src/constants/june-so";
import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";
import {
  SNACKMAGIC,
  STORES,
  SUPERIORDISHES,
  SWAGMAGIC,
} from "@stadium/shared/src/constants/brands";
import {
  createWorkspaceNudgeFlag,
  workspaceSidebarFlag,
  workspaceWalletFlag,
  requestToJoinWorkspaceFlag,
  hideCreateNewWorkspaceFlag,
} from "@stadium/services/launch-darkly/constants";
import { getPathnameFromUrl } from "@stadium/helpers/routes";
import useCreateWorkspaceNudge from "@stadium/shared/src/components/header/profile-dropdown/use-create-workspace-nudge";

import defaultStyles from "./main.module.scss";

const emptyObj = {};
const defaultFn = () => {};

const ResponsiveProfileDropdown = ({
  logout,
  profileData,
  isMinifiedAccountDropdown = false,
  accessibilityEnabled = true,
  styles = defaultStyles,
  isNewSwagmagicHost = false,
}) => {
  const {
    [workspaceSidebarFlag]: workspaceSidebarFlagEnabled,
    [workspaceWalletFlag]: workspaceWalletFlagEnabled,
    [requestToJoinWorkspaceFlag]: requestToJoinWorkspaceFlagEnabled,
    [createWorkspaceNudgeFlag]: createWorkspaceNudgeFlagEnabled,
  } = useFlags();
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const { trackEvent } = useJuneSoEventTracker({
    services: ACCOUNT_DROPDOWN_CTA_SERVICES,
    elementPosition: ACCOUNT_DROPDOWN,
  });
  const withAnalytics = useWithAnalytics();

  const onProfileMenuOpen = useCallback(() => {
    withAnalytics(undefined, {
      track: true,
      target: HEADER_ANALYTICS_TARGET.PROFILE_MENU,
      action: ANALYTICS_EVENT_ACTION_OPENED,
    })();
  }, [withAnalytics]);

  const { site } = useStadiumSharedGlobalConfigContext();

  const byStadiumSite = isStadiumSite(site);

  const defaultProfileDataLinks = useMemo(() => getProfileData({ trackEvent }), [trackEvent]);

  const miniProfileDataLinks = useMemo(
    () => getMiniAccountDropdownData({ trackEvent, workspaceWalletFlagEnabled }),
    [trackEvent],
  );

  const {
    firstName,
    email,
    rewardPoints,
    headLabel,
    vipBadge = null,
    label,
    activeWorkspaceSlug,
    workspaces,
    onWorkspaceSelection = null,
    canViewShops,
  } = profileData || emptyObj;
  const { closeCreateWorkspaceModal, handleCreateWorkspaceClick, showCreateWorkspaceModal } =
    useCreateWorkspaceNudge({
      isNudgeEnabled: createWorkspaceNudgeFlagEnabled,
      workspaces,
    });

  const navDataLinks = useMemo(
    () =>
      getNavAccountDropdownData({
        trackEvent,
        isStore: site === STORES,
        accessibilityEnabled,
        workspaceSlug: activeWorkspaceSlug,
        workspaceSidebarFlagEnabled,
        canViewShops,
        isMobileDevice,
      }),
    [trackEvent, site, activeWorkspaceSlug, workspaceSidebarFlagEnabled],
  );

  const activeWorkspace = useMemo(
    () => workspaces?.find(({ slug }) => slug === activeWorkspaceSlug),
    [workspaces, activeWorkspaceSlug],
  );

  const renderLink = ({
    to,
    linkClassName,
    label,
    onLinkClick = null,
    renderPillTag = defaultFn,
  }) => (
    <HeaderLink
      url={to}
      urlText={label}
      className={linkClassName}
      onClick={onLinkClick}
      target={!byStadiumSite ? "_blank" : ""}
      renderPillTag={renderPillTag}
      eventLocationAnalytics={DEFAULT_EVENT_LOCATION_ANALYTICS}
    />
  );

  const firstNameToShow = firstName?.split(" ")[0];

  const renderNewHead = () => (
    <div
      className={`${styles.accountContainerInner} ${styles.accountContainerInnerFlex} ${styles.swagmagicAccountContainerInner}`}
    >
      <div className={styles.avtarArea}>
        <Avatar user={profileData} workspace={activeWorkspace} showAvatarInResponsive />
      </div>
      <div className={styles.emailAdd}>{email} </div>
    </div>
  );

  const renderHead = () => (
    <>
      <div className={`${styles.accountContainerInner} ${styles.accountContainerInnerFlex}`}>
        <div className={styles.avtarArea}>
          <Avatar user={profileData} workspace={activeWorkspace} />
        </div>
        <div className={styles.accountInfo}>
          <div className={styles.accountName}>{firstNameToShow}</div>
          {workspaceSidebarFlagEnabled && activeWorkspace?.name && (
            <div className={styles.companyName}>{activeWorkspace.name}</div>
          )}
          {!workspaceSidebarFlagEnabled && <div className={styles.emailAdd}>{email} </div>}
          {typeof rewardPoints === "number" && (
            <div className={styles.userPointsResponsiveText}>{rewardPoints} PTS</div>
          )}
        </div>
        {vipBadge}
      </div>
      <div className={styles.dividerLine} />
    </>
  );

  const profileDataLinks = useMemo(() => {
    if (byStadiumSite) {
      return navDataLinks;
    }
    return isMinifiedAccountDropdown ? miniProfileDataLinks : defaultProfileDataLinks;
  }, [isMinifiedAccountDropdown, byStadiumSite, navDataLinks]);

  const programList = useMemo(
    () =>
      profileDataLinks.map((data) => (
        <ProfileList
          {...data}
          styles={styles}
          renderLink={renderLink}
          key={`item-${data?.title}`}
        />
      )),
    [profileDataLinks],
  );

  const getWorkspaceSelectionRoute = useCallback(
    ({ selectedWorkspaceSlug, currentPath }) => {
      if (onWorkspaceSelection) {
        return onWorkspaceSelection?.(selectedWorkspaceSlug, currentPath);
      }
      return currentPath.replace(activeWorkspaceSlug, selectedWorkspaceSlug);
    },
    [activeWorkspaceSlug, onWorkspaceSelection],
  );

  const renderWorkspace = ({ name, iconUrl, isActive, isDefault }) => (
    <>
      <div className={styles.nameBlock}>
        {iconUrl ? (
          <Multimedia
            type="image"
            src={iconUrl}
            alt={name}
            className={styles.companyLogo}
            isNextImage={false}
          />
        ) : (
          <span className={styles.iconDefault} />
        )}
        <span className={`${styles.companyName} ${isActive ? styles.active : ""}`}>{name}</span>
        {isDefault && <span className={styles.defaultText}>Default</span>}
      </div>
      {isActive && <span className={styles.iconTick} />}
    </>
  );

  const workspaceList = (
    <>
      <ul className={styles.workSpaceSection}>
        {workspaces?.map((workspace) => {
          const { id, slug } = workspace;
          const isActive = activeWorkspace?.id === id;

          return site === STORES ? (
            <li className={styles.listItem} key={id}>
              <Button
                styleType="link"
                onClick={() => {
                  onWorkspaceSelection?.(slug, getPathnameFromUrl(window.location.pathname));
                }}
                className={styles.link}
              >
                {renderWorkspace({ ...workspace, isActive })}
              </Button>
            </li>
          ) : (
            <li className={styles.listItem} key={id}>
              <LinkButton
                styleType="no-style"
                href={getWorkspaceSelectionRoute({
                  selectedWorkspaceSlug: slug,
                  currentPath: getPathnameFromUrl(window.location.pathname),
                })}
                className={styles.link}
              >
                {renderWorkspace({ ...workspace, isActive })}
              </LinkButton>
            </li>
          );
        })}
        <FeatureFlagWrapper
          flag={hideCreateNewWorkspaceFlag}
          fallback={
            <li className={styles.listItem}>
              <Button
                styleType="no-style"
                onClick={handleCreateWorkspaceClick}
                className={`${styles.link} ${styles.newWorkSpaceLink}`}
              >
                <span className={styles.iconAdd} />
                <span className={styles.text}>Create New Workspace</span>
              </Button>
            </li>
          }
        ></FeatureFlagWrapper>
      </ul>
      {requestToJoinWorkspaceFlagEnabled && (
        <div className={styles.workspaceBanner}>
          Looks like your team’s already on <br className={styles.break} />
          Stadium!{" "}
          <LinkButton styleType="no-style" href="/" className={styles.link}>
            View Workspaces to join
          </LinkButton>
        </div>
      )}
    </>
  );

  const labelStyle = (() => {
    switch (site) {
      case SNACKMAGIC:
        return styles.snackmagicLabelStyle;
      case SUPERIORDISHES:
        return styles.superdishesLabelStyle;
      case SWAGMAGIC:
        return styles.swagmagicLabelStyle;
      default:
        return styles.defaultLabelStyle;
    }
  })();

  const list = useMemo(
    () => [
      ...programList,
      ...(workspaceSidebarFlagEnabled && byStadiumSite ? [workspaceList] : []),
    ],
    [programList, workspaceSidebarFlagEnabled, profileData],
  );

  const showHelpCenterCta =
    !(workspaceSidebarFlagEnabled && byStadiumSite && isMobileDevice) || isNewSwagmagicHost;

  return (
    <>
      {showCreateWorkspaceModal && <CreateWorkspaceModal closeModal={closeCreateWorkspaceModal} />}
      <div
        className={`${styles.userDDContainer} ${labelStyle} ${
          workspaceSidebarFlagEnabled ? styles.userContainerMobile : ""
        } ${isNewSwagmagicHost ? styles.swagmagicDropdownContainer : ""}`}
      >
        <SliderDropdown
          styles={styles}
          label={label ?? renderHead()}
          backLabel={headLabel ?? renderHead()}
          className={styles.profileDDResponsiveWrapper}
          ariaLabel="Profile Dropdown"
          onLabelClick={onProfileMenuOpen}
        >
          {isNewSwagmagicHost && renderNewHead()}
          <ProfileDdBody
            className={`${styles.userDropdownMenuContainerMobile} ${
              isNewSwagmagicHost ? styles.userDropdownMenuContainerMobileSW : ""
            }`}
            styles={styles}
            programList={list}
            emailAtBottom={
              byStadiumSite && !isNewSwagmagicHost && workspaceSidebarFlagEnabled && email
            }
            showHelpCenterCta={showHelpCenterCta}
            helpCenterCta={
              <HelpCenterLink eventLocationAnalytics={DEFAULT_EVENT_LOCATION_ANALYTICS} />
            }
            logoutButton={
              <Button
                className={classnames({ [styles.logoutButton]: workspaceSidebarFlagEnabled })}
                onClick={(e) => {
                  trackEvent({ target: "logout" });
                  logout(e);
                }}
                styleType={
                  workspaceSidebarFlagEnabled && byStadiumSite ? "primary-outline" : "link"
                }
                eventLocationAnalytics={DEFAULT_EVENT_LOCATION_ANALYTICS}
              >
                LOGOUT
              </Button>
            }
            isNewSwagmagicHost={isNewSwagmagicHost}
          />
        </SliderDropdown>
      </div>
    </>
  );
};

export default ResponsiveProfileDropdown;
